import moment from 'moment-timezone';
import {List} from 'immutable';
import numeral from 'numeral';

import createSelector from 'web-app/util/custom-create-selector';
import * as ChildSelectors from 'web-app/react/entities/children/selectors';
import PlanBehaviorIds from 'web-app/behaviors/plan-behavior-ids';
import * as CostHelpers from 'web-app/react/entities/plans/cost-helpers';
import {roundToDecimals} from 'web-app/util/math';
import {isNumber} from 'web-app/util/parsers';
import PlanModel from 'web-app/react/entities/plans/model';
import * as PlanConstants from 'web-app/react/entities/plans/constants';
import {hasValue} from 'web-app/util/typescript';
import * as ChildProfileSelectors from 'web-app/react/profile/child-profile/plans-and-invoices/selectors';

import * as SharedSelectors from './shared-selectors';

const didFetchPreview = state => state.plans.managePlan.didFetchPreview;
const fetchingPreview = state => state.plans.managePlan.fetchingPreview;

const availableSessions = SharedSelectors.availableSessions;

const planUsedSessions = SharedSelectors.planUsedSessions;
const planUsedProducts = SharedSelectors.planUsedProducts;

export const child = state => ChildSelectors.getChildByProps(state, {childId: SharedSelectors.getChildId(state)});
export const childMonthsOld = createSelector(child, child =>
    child && child.birthday ? moment().diff(moment(child.birthday, 'YYYY-MM-DD'), 'months') : 0,
);

export const childPlans = state => ChildProfileSelectors.plans(state, {referenceId: SharedSelectors.getChildId(state)});

export const childMonthsOldAtPlanStart = createSelector(child, SharedSelectors.validOn, (child, validOn) =>
    child && child.birthday ? moment(validOn).diff(moment(child.birthday, 'YYYY-MM-DD'), 'months') : 0,
);

export const standaloneSessions = createSelector(availableSessions, sessions =>
    sessions.filter(session => session.sessionType !== 'package'),
);

export const previewWarnings = createSelector(SharedSelectors.planBehaviors, previewBehaviors => {
    const planWarningsBehavior = previewBehaviors.behaviorForId(PlanBehaviorIds.ShowPlanWarnings);
    if (planWarningsBehavior) {
        return List(planWarningsBehavior.payload.warnings);
    } else {
        return List();
    }
});

export const canDeletePlan = createSelector(SharedSelectors.originalPlanBehaviors, originalPlanBehaviors =>
    originalPlanBehaviors.includes(PlanBehaviorIds.CanDeletePlan),
);

export const canAddPlanParts = createSelector(SharedSelectors.planBehaviors, originalPlanBehaviors =>
    originalPlanBehaviors.includes(PlanBehaviorIds.CanAddPlanParts),
);

const discountIsInvalid = (discount, billing, discountPresets) => {
    if (discount.origin === CostHelpers.DISCOUNT_ORIGIN_PRESET) {
        const preset =
            discountPresets && discountPresets.find(preset => preset.discountPresetId === discount.discountPresetId);
        return (
            preset &&
            numeral(roundToDecimals(preset.amount / 100, 4)).difference(roundToDecimals(discount.amount, 4)) !== 0
        );
    } else if (discount.isPercent) {
        return discount.amount > 1;
    } else if (CostHelpers.billingIsActuals(billing)) {
        return discount.isSurcharge;
    }

    return false;
};

const hasInvalidDiscounts = createSelector(
    SharedSelectors.planParts,
    SharedSelectors.allDiscountPresets,
    (planParts, discountPresets) =>
        planParts.some(part =>
            part.discounts.some(discount => discountIsInvalid(discount, part.billing, discountPresets)),
        ),
);

export const showDailyDiscount = createSelector(
    child,
    SharedSelectors.planBehaviors,
    (child, planBehaviors) =>
        hasValue(child) && planBehaviors && planBehaviors.includes(PlanBehaviorIds.CanAddDailyDiscounts),
);

const isSessionUnAvailable = (planUsedSession, standaloneSessions, packages) => {
    const usedInSessions = standaloneSessions.find(as => as.sessionId === planUsedSession.sessionId);
    const usedInPackages = packages.find(as => as.sessionId === planUsedSession.sessionId);
    return !usedInSessions && !usedInPackages;
};

export const planUsesUnavailableSessions = createSelector(
    planUsedSessions,
    standaloneSessions,
    SharedSelectors.packages,
    (planUsedSessions, standaloneSessions, packages) =>
        planUsedSessions.some(pus => isSessionUnAvailable(pus, standaloneSessions, packages)),
);

const isProductUnAvailable = (planUsedProduct, availableProducts) => {
    const usedInProducts = availableProducts.find(as => as.productId === planUsedProduct.productId);

    return !usedInProducts;
};

export const planUsesUnavailableProducts = createSelector(
    planUsedProducts,
    SharedSelectors.availableProducts,
    (planUsedProducts, availableProducts) => planUsedProducts.some(pup => isProductUnAvailable(pup, availableProducts)),
);

const doPreview = createSelector(
    SharedSelectors.showEdit,
    child,
    hasInvalidDiscounts,
    SharedSelectors.isValidPlanSettings,
    (showEdit, child, hasInvalidDiscounts, isValidPlan) =>
        Boolean(showEdit) && Boolean(child) && !hasInvalidDiscounts && isValidPlan,
);

export const showPublicFunding = createSelector(
    child,
    SharedSelectors.planBehaviors,
    (child, planBehaviors) =>
        Boolean(child) && planBehaviors && planBehaviors.includes(PlanBehaviorIds.ShowPublicFundingOnPlan),
);

export const disablePublicFunding = createSelector(
    doPreview,
    showPublicFunding,
    (doPreview, showPublicFunding) => !doPreview || !showPublicFunding,
);

const isInvalidPublicFunding = createSelector(SharedSelectors.planPublicFundingSettings, publicFunding => {
    return (
        publicFunding &&
        (publicFunding.method === PlanConstants.PUBLIC_FUNDING_TYPE.UNKNOWN ||
            (publicFunding.method !== PlanConstants.PUBLIC_FUNDING_TYPE.NONE &&
                !isNumber(publicFunding.hours) &&
                !isNumber(publicFunding.minutes)))
    );
});

export const disableWeekdays = createSelector(
    SharedSelectors.isValidPlanSettings,
    isValidPlanSettings => !isValidPlanSettings,
);

export const isInvalidPlan = createSelector(
    doPreview,
    planUsesUnavailableSessions,
    planUsesUnavailableProducts,
    hasInvalidDiscounts,
    isInvalidPublicFunding,
    SharedSelectors.hasInvalidSessionBookings,
    (
        doPreview,
        planUsesUnavailableSessions,
        planUsesUnavailableProducts,
        hasInvalidDiscounts,
        isInvalidPublicFunding,
        hasInvalidSessionBookings,
    ) =>
        !doPreview ||
        planUsesUnavailableSessions ||
        planUsesUnavailableProducts ||
        isInvalidPublicFunding ||
        hasInvalidSessionBookings ||
        hasInvalidDiscounts,
);

export const canSavePlan = createSelector(
    didFetchPreview,
    fetchingPreview,
    SharedSelectors.planBehaviors,
    isInvalidPlan,
    (didFetchPreview, fetchingPreview, previewBehaviors, isInvalidPlan) =>
        didFetchPreview && !fetchingPreview && previewBehaviors.includes(PlanBehaviorIds.CanSavePlan) && !isInvalidPlan,
);

export const previewPlan = createSelector(
    SharedSelectors.getChildId,
    SharedSelectors.validOn,
    SharedSelectors.validTo,
    SharedSelectors.editPlan,
    (childId, validOn, validTo, editPlan) => {
        // We must add a day before we send the plan to the backend
        const validToMoment = moment(validTo, 'YYYY-MM-DD');
        const to = validToMoment.isValid() ? validToMoment.add(1, 'day').format('YYYY-MM-DD') : undefined;

        const newPlan = {
            ...PlanModel.toAPI(editPlan),
            childId,
            from: validOn,
            to,
        };

        return newPlan;
    },
);
